import { useState } from 'react';
import { ChevronDown, ChevronRight, GitMerge, CheckCheck, Wrench, AlertCircle, MessageSquareWarning } from 'lucide-react';
import type { RankSequenceDivergence } from '../../types/planning';
import {
  updateDivergenceResolution,
} from '../../lib/planningService';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DivergencePanelProps {
  divergences: RankSequenceDivergence[];
  /** May this person settle a divergence? It takes the authority to authorize the plan. */
  canResolve: boolean;
  onResolutionChange: () => void;
}

// ─── Resolution status config ─────────────────────────────────────────────────

const RESOLUTION_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  detected:  { label: 'Open',      bg: 'bg-[var(--mustard-dim)]',  text: 'text-[var(--mustard)]' },
  accepted:  { label: 'Accepted',  bg: 'bg-[var(--slate-dim)]',   text: 'text-[var(--text-sec)]' },
  mitigated: { label: 'Mitigated', bg: 'bg-[var(--green-dim)]',  text: 'text-[var(--green)] dark:text-[var(--text-ter)]' },
  disputed:  { label: 'Disputed',  bg: 'bg-[var(--coral-dim)]',    text: 'text-[var(--coral)]'  },
};

// Disputed counts as "resolved" (not shown in open list) but displayed differently
// Disputed stays open: canon 104 §4, a dispute holds the plan until it is settled.
const RESOLVED_STATUSES = new Set(['accepted', 'mitigated']);

const KIND_LABELS: Record<string, string> = {
  lower_rank_ahead: 'Lower rank ahead',
  plan_order_changed: 'Plan order changed',
};

function ResolutionBadge({ status }: { status: string | null | undefined }) {
  const cfg = RESOLUTION_CONFIG[status ?? 'detected'] ?? RESOLUTION_CONFIG.detected;
  return (
    <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
}

// ─── Single divergence row ────────────────────────────────────────────────────

function DivergenceRow({
  divergence,
  canResolve,
  startExpanded,
  onResolutionChange,
}: {
  divergence: RankSequenceDivergence;
  canResolve: boolean;
  startExpanded: boolean;
  onResolutionChange: () => void;
}) {
  const [expanded, setExpanded] = useState(startExpanded);
  const [reason, setReason] = useState('');
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const status = divergence.resolution_status ?? 'detected';
  const isResolved = RESOLVED_STATUSES.has(status);

  // Extract readable info from payload (canon Doc 19 §4 defines payload shape)
  const payload = divergence.payload ?? {};
  const higherRanked = (payload.higher_ranked_title ?? payload.higher_item_id ?? '—') as string;
  const lowerRanked  = (payload.lower_ranked_title  ?? payload.lower_item_id  ?? '—') as string;
  const rawKind      = (payload.divergence_kind ?? payload.kind ?? 'rank_inversion') as string;
  const kind         = KIND_LABELS[rawKind] ?? rawKind;
  const needsReason  = reason.trim() === '';

  const handleAction = async (action: 'accept' | 'mitigate' | 'dispute') => {
    setActing(true);
    setActionError(null);
    const resolution = action === 'accept' ? 'accepted' : action === 'mitigate' ? 'mitigated' : 'disputed';
    const { error: err } = await updateDivergenceResolution(divergence.id, resolution, reason);
    if (err) { setActionError(err); setActing(false); return; }
    setActing(false);
    onResolutionChange();
  };

  return (
    <div className={`border  overflow-hidden transition-colors ${
      isResolved ? 'border-[var(--rule)] bg-[var(--surface)]/50' : 'border-[var(--mustard)] bg-[var(--mustard-dim)]/30'
    }`}>
      {/* Summary row */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-black/5 transition-colors"
      >
        {expanded
          ? <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
          : <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
        }
        <GitMerge className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
        <span className="text-sm text-[var(--text-sec)] truncate flex-1">
          <span className="font-medium">{higherRanked}</span>
          <span className="text-[var(--text-ter)] mx-1.5">vs</span>
          <span className="font-medium">{lowerRanked}</span>
        </span>
        <span className="text-xs text-[var(--text-ter)] mr-2 hidden sm:inline">{kind}</span>
        <ResolutionBadge status={status} />
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-3 space-y-2 border-t border-current/10">
          <div className="grid grid-cols-2 gap-2 pt-2 text-xs text-[var(--text-ter)]">
            <div>
              <span className="font-medium text-[var(--text-sec)]">Higher ranked</span>
              <p className="mt-0.5 truncate">{higherRanked}</p>
            </div>
            <div>
              <span className="font-medium text-[var(--text-sec)]">Lower ranked</span>
              <p className="mt-0.5 truncate">{lowerRanked}</p>
            </div>
            {Boolean(payload.explanation) && (
              <div className="col-span-2">
                <span className="font-medium text-[var(--text-sec)]">Explanation</span>
                <p className="mt-0.5">{String(payload.explanation)}</p>
              </div>
            )}
          </div>

          {divergence.resolution_reason && (
            <p className="text-xs text-[var(--text-ter)]">
              <span className="font-medium text-[var(--text-sec)]">Why</span> {divergence.resolution_reason}
            </p>
          )}

          {/* Action buttons — only shown for open divergences, to those who may settle them */}
          {!isResolved && canResolve && (
            <div className="space-y-2 pt-1">
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Why is this right for the plan?"
              aria-label="Why"
              className="w-full border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1.5 text-xs text-[var(--text-pri)]"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={acting || needsReason}
                onClick={() => void handleAction('accept')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--slate)] text-white text-xs font-medium hover:bg-[var(--slate)] disabled:opacity-50"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Accept
              </button>
              <button
                type="button"
                disabled={acting || needsReason}
                onClick={() => void handleAction('mitigate')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)] text-xs font-medium hover:bg-[var(--green-dim)] disabled:opacity-50"
              >
                <Wrench className="h-3.5 w-3.5" />
                Mark mitigated
              </button>
              <button
                type="button"
                disabled={acting || needsReason}
                onClick={() => void handleAction('dispute')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--coral)] text-[var(--coral)] text-xs font-medium hover:bg-[var(--coral-dim)] disabled:opacity-50"
                title="Dispute — flag this divergence as incorrectly detected or based on invalid data"
              >
                <MessageSquareWarning className="h-3.5 w-3.5" />
                Dispute
              </button>
              {actionError && (
                <span className="text-xs text-[var(--coral)] ml-2">{actionError}</span>
              )}
            </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

export function DivergencePanel({
  divergences,
  canResolve,
  onResolutionChange,
}: DivergencePanelProps) {
  const open     = divergences.filter((d) => !RESOLVED_STATUSES.has(d.resolution_status ?? 'detected'));
  const resolved = divergences.filter((d) => RESOLVED_STATUSES.has(d.resolution_status ?? 'detected'));

  if (divergences.length === 0) return null;

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-5 py-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-[var(--text-pri)] flex items-center gap-2">
          <GitMerge className="h-4 w-4 text-[var(--text-ter)]" />
          Rank Divergences
          {open.length > 0 && (
            <span className="ml-1 inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-[var(--mustard-dim)] text-[var(--mustard)]">
              <AlertCircle className="h-3 w-3" />
              {open.length} open
            </span>
          )}
        </h2>
        <span className="text-xs text-[var(--text-ter)]">{divergences.length} total</span>
      </div>

      {/* Open divergences first */}
      {open.length > 0 && (
        <div className="space-y-2">
          {open.map((d) => (
            <DivergenceRow
              key={d.id}
              divergence={d}
              canResolve={canResolve}
              startExpanded={canResolve}
              onResolutionChange={onResolutionChange}
            />
          ))}
        </div>
      )}

      {/* Resolved (collapsed summary) */}
      {resolved.length > 0 && (
        <details className="group">
          <summary className="cursor-pointer text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] select-none list-none flex items-center gap-1">
            <ChevronRight className="h-3 w-3 group-open:rotate-90 transition-transform" />
            {resolved.length} resolved divergence{resolved.length !== 1 ? 's' : ''}
          </summary>
          <div className="mt-2 space-y-1.5">
            {resolved.map((d) => (
              <DivergenceRow
                key={d.id}
                divergence={d}
                canResolve={false}
                startExpanded={false}
                onResolutionChange={onResolutionChange}
              />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
